import { differenceInDays, differenceInHours, differenceInMinutes, differenceInSeconds } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

/** Shared threshold: table bold, map dash, and Unknown status. */
export const STALE_MINUTES = 60;

export const STATUS = {
    OK: 'ok',
    DISCREPANCY: 'discrepancy',
    UNKNOWN: 'unknown',
};

export const STATUS_CLOSED_REGULAR = 'Closed During Regular Hours';
export const STATUS_OPEN_OUTSIDE = 'Open Outside Regular Hours';

/** Operational colors (same as pre-elite-pass map / table). */
export const COLORS = {
    accent: '#477fdf',
    accentTab: '#2196F3',
    selected: '#003F75',
    charcoal: '#111111',
    closed: 'red',
    partialClosed: '#FFA200',
    openOutside: '#00FFFF',
    ok: '#1c9600',
    unknown: '#6B7280',
    staleText: 'red',
};

export const getSensorAgeMinutes = (timestamp) => {
    if (!timestamp) return Number.POSITIVE_INFINITY;
    const utcDate = new Date(timestamp);
    const estDate = toZonedTime(utcDate, 'America/New_York');
    const currentDateEST = toZonedTime(new Date(), 'America/New_York');
    return differenceInMinutes(currentDateEST, estDate);
};

export const isSensorStale = (timestamp, thresholdMinutes = STALE_MINUTES) =>
    getSensorAgeMinutes(timestamp) > thresholdMinutes;

export const getTrustStatus = (row) => {
    if (isSensorStale(row.Timestamp)) return STATUS.UNKNOWN;
    if (row.IsDiscrepency) return STATUS.DISCREPANCY;
    return STATUS.OK;
};

export const formatRelativeSensorLabel = (timestamp) => {
    const utcDate = new Date(timestamp);
    const estDate = toZonedTime(utcDate, 'America/New_York');
    const currentDateEST = toZonedTime(new Date(), 'America/New_York');

    const differenceMinutes = differenceInMinutes(currentDateEST, estDate);
    const differenceSeconds = Math.max(0, differenceInSeconds(currentDateEST, estDate));
    const differenceHours = differenceInHours(currentDateEST, estDate);
    const differenceDays = differenceInDays(currentDateEST, estDate);
    const stale = differenceMinutes > STALE_MINUTES;

    let label = '';
    if (differenceMinutes >= 1440) {
        label = differenceDays === 1 ? '1 day ago' : `${differenceDays} days ago`;
    } else if (differenceMinutes >= 60) {
        label = differenceHours === 1 ? '1 hour ago' : `${differenceHours} hours ago`;
    } else if (differenceMinutes <= 0) {
        label = differenceSeconds === 1 ? '1 second ago' : `${differenceSeconds} seconds ago`;
    } else {
        label = differenceMinutes === 1 ? '1 minute ago' : `${differenceMinutes} minutes ago`;
    }

    return { label, stale };
};

export const SEVERITY = {
    CLOSED_REGULAR: 0,
    PARTIAL_CLOSED_REGULAR: 1,
    PARTIAL_OPEN_CLOSED_HOURS: 2,
};

/** Classify against all gates in the unit so 1 closed + 1 open = partial (orange), not red. */
export const classifyUnitDiscrepancy = (gates) => {
    const totalGates = gates.length;
    const closedRegularCount = gates.filter((g) => g.StateDescription === STATUS_CLOSED_REGULAR).length;
    const openOutsideCount = gates.filter((g) => g.StateDescription === STATUS_OPEN_OUTSIDE).length;

    if (closedRegularCount > 0) {
        const fullyClosed = closedRegularCount === totalGates;
        return {
            severity: fullyClosed ? SEVERITY.CLOSED_REGULAR : SEVERITY.PARTIAL_CLOSED_REGULAR,
            statusLabel: fullyClosed
                ? 'Closed During Regular Hours'
                : 'Partially Closed During Regular Hours',
            gateSummary: `${closedRegularCount} of ${totalGates} gates closed`,
            color: fullyClosed ? COLORS.closed : COLORS.partialClosed,
        };
    }

    if (openOutsideCount > 0) {
        return {
            severity: SEVERITY.PARTIAL_OPEN_CLOSED_HOURS,
            statusLabel: 'Partially Open During Closed Hours',
            gateSummary: `${openOutsideCount} of ${totalGates} gates open`,
            color: COLORS.openOutside,
        };
    }

    return null;
};

/** Group raw gate rows into tenant + unit buckets (one entry per unit). */
export const groupRowsByUnit = (rows = []) => {
    const byUnit = {};

    rows.forEach((row) => {
        const unitKey = `${row.SQLTenantName}|||${row.SQLUnitName}`;
        if (!byUnit[unitKey]) {
            byUnit[unitKey] = {
                id: unitKey,
                SQLTenantName: row.SQLTenantName,
                SQLUnitName: row.SQLUnitName,
                SQLLocationName: row.SQLLocationName,
                RawStartTimeString: row.StartTimeString,
                RawEndTimeString: row.EndTimeString,
                gates: [],
            };
        }
        byUnit[unitKey].gates.push({
            ...row,
            IsStale: isSensorStale(row.Timestamp),
        });
    });

    return Object.values(byUnit);
};

/** Discrepancy only counts when a fresh (non-stale) gate reports it, same as the map panel. */
export const hasLiveDiscrepancy = (gates) => gates.some((g) => g.IsDiscrepency && !g.IsStale);

export const getMarkerBackground = (stateDescriptions = []) => {
    const hasClosed = stateDescriptions.includes(STATUS_CLOSED_REGULAR);
    const hasOpenOutside = stateDescriptions.includes(STATUS_OPEN_OUTSIDE);

    if (hasClosed) {
        return stateDescriptions.every((status) => status === STATUS_CLOSED_REGULAR)
            ? COLORS.closed
            : COLORS.partialClosed;
    }
    if (hasOpenOutside) {
        return COLORS.openOutside;
    }
    if (stateDescriptions.includes('Open') || stateDescriptions.includes('Closed')) {
        return COLORS.ok;
    }
    return COLORS.charcoal;
};
